import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, CircularProgress, Divider, LinearProgress, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';

const styles = {
  card: { width: '100%', boxShadow: 2 },
  cardMobileContent: { display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 2, '&:last-child': { pb: 2 } },
  mobileLabel: { fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.disabled' },
  mobileProgressBar: { height: 6, borderRadius: 3, bgcolor: 'divider', '& .MuiLinearProgress-bar': { borderRadius: 3 } },
  mobilePercent: { fontSize: '0.8rem', fontWeight: 700, color: 'success.main' },
  mobileStats: { display: 'flex', gap: 1.5, alignItems: 'center' },
  mobileStat: { display: 'flex', alignItems: 'center', gap: 0.5 },
  dot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  mobilePending: { fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 },
  skeletonRow: { display: 'flex', flexDirection: 'column', gap: 1 },
  legendMobile: { display: 'flex', flexDirection: 'column', gap: 1.5 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 1 },
  chartWrap: { position: 'relative', display: 'inline-flex' },
  chartOverlay: { position: 'absolute', left: 0, '& circle': { strokeLinecap: 'round' } },
  chartLabel: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25 },
  cardDesktop: { width: '100%', height: { xs: 500, md: '60vh' }, maxHeight: 560, boxShadow: 2, overflow: 'hidden' },
  cardDesktopContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: '100%', pt: 3, pb: '24px !important', px: 3 },
  skeletonDesktopChart: { mt: 'auto', mb: 'auto' },
  chartCenter: { flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  chartCenterLabel: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  legend: { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mb: { lg: 4 } },
  legendRow: { display: 'flex', justifyContent: 'center', gap: { xs: 1.5, sm: 3 }, width: '100%', px: 1 },
  legendItemDesktop: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minWidth: 0 },
  legendDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  legendDotRow: { display: 'flex', alignItems: 'center', gap: 0.5 },
  legendValue: { fontSize: { xs: '1rem', sm: '1.25rem' } },
  legendLabel: { fontSize: { xs: '0.65rem', sm: '0.75rem' } },
};

export default function SummaryCard({ loading, total = 0, pending = 0 }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isShortScreen = useMediaQuery('(max-height: 700px)');
  const chartSize = isMobile ? 120 : isShortScreen ? 160 : 220;

  const completed = total - pending;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  if (isMobile) {
    return (
      <Card sx={styles.card}>
        <CardContent sx={styles.cardMobileContent}>
          <Typography sx={styles.mobileLabel}>{t('bruno.home.tasks_title', 'Tasks')}</Typography>

          {loading ? (
            <>
              <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3 }} />
              <Skeleton variant="text" width="80%" height={14} />
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={styles.mobilePercent}>{percentage}%</Typography>
                <LinearProgress variant="determinate" value={percentage} color="success" sx={styles.mobileProgressBar} />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={styles.mobileStat}>
                  <Box sx={{ ...styles.dot, bgcolor: 'success.main' }} />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'success.main' }}>{completed} {t('bruno.home.completed', 'Completed')}</Typography>
                </Box>
                <Box sx={styles.mobileStat}>
                  <Box sx={{ ...styles.dot, bgcolor: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{pending} {t('bruno.home.pending', 'Pending')}</Typography>
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const legendItems = [
    { value: completed, label: t('bruno.home.completed', 'Completed'), color: 'success.main', dot: 'success.main' },
    { value: pending, label: t('bruno.home.pending', 'Pending'), color: 'text.secondary', dot: 'text.disabled' },
    { value: total, label: t('bruno.home.total_tasks', 'Total'), color: 'text.primary', dot: 'text.primary' },
  ];

  return (
    <Card sx={styles.cardDesktop}>
      <CardContent sx={styles.cardDesktopContent}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          {t('bruno.home.tasks_title', 'Tasks')}
        </Typography>

        {loading ? (
          <>
            <Skeleton variant="circular" width={chartSize} height={chartSize} sx={styles.skeletonDesktopChart} />
            <Box sx={{ width: '100%' }}>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width={160} sx={{ mx: 'auto' }} />
            </Box>
          </>
        ) : (
          <>

            <Box sx={styles.chartCenter}>
              <Box sx={styles.chartWrap}>
                <CircularProgress variant="determinate" value={100} size={chartSize} thickness={5} sx={{ color: 'divider' }} />
                <CircularProgress variant="determinate" value={percentage} size={chartSize} thickness={5} color="success" sx={styles.chartOverlay} />
                <Box sx={styles.chartCenterLabel}>
                  <Typography variant="h4" fontWeight={700} color="success.main" lineHeight={1}>{percentage}%</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={styles.legend}>
              <Divider sx={{ width: '100%' }} />
              <Box sx={styles.legendRow}>
                {legendItems.map((item, i, arr) => (
                  <React.Fragment key={item.label}>
                    <Box sx={styles.legendItemDesktop}>
                      <Typography fontWeight={700} color={item.color} sx={styles.legendValue}>{item.value}</Typography>
                      <Box sx={styles.legendDotRow}>
                        <Box sx={{ ...styles.legendDot, bgcolor: item.dot }} />
                        <Typography variant="caption" color={item.color} sx={styles.legendLabel}>{item.label}</Typography>
                      </Box>
                    </Box>
                    {i < arr.length - 1 && <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
